import * as ProRegularSvgIcons from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { format, parseISO } from 'date-fns'
import { useFormikContext } from 'formik'
import React from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { Tooltip } from 'react-tooltip'
import * as Api from 'src/api'
import { DownloadIcon } from 'src/assets/icons/customIcons/Download'
import { formatFileSize } from 'src/helpers/fns'
import { useApi } from 'src/helpers/hooks'
import { useAuthenticatedHeaders } from 'src/hooks/auth/app'
import { useLocale } from 'src/hooks/locale/locale'
import { useDateLocale, useTranslatable } from 'src/hooks/locale/utils'
import Warning from 'src/imgs/classroom_icon.svg'
import { useTheme } from 'src/state/providers/Theme'
import { Button } from 'src/tailwind/components/Button'
import { Section } from 'src/tailwind/components/Section'
import * as Table from 'src/tailwind/components/Table'
import { Form } from 'src/views/components/forms/formik/Form'
import { FormError } from 'src/views/components/forms/formik/FormError'
import SelectInput from 'src/views/components/forms/formik/SelectInput'
import TextInput from 'src/views/components/forms/formik/TextInput'
import { MediaFilesDownload } from 'src/views/components/MediaFilesDownload'
import NoContent from 'src/views/components/NoContent'
import { PaginationBottom, PaginationTop } from 'src/views/components/Pagination'
import UserPhoto from 'src/views/components/UserPhoto'
import SuspenseWrapper from 'src/views/includes/SuspenseWrapper'

interface FormikValues {
  readonly query: string
  readonly status: string
  readonly storage: string
}
export default function AdministratorFilesPage() {
  const t = useTranslatable()

  return (
    <SuspenseWrapper title={t('common:files')}>
      <PageContent />
    </SuspenseWrapper>
  )
}

function PageContent() {
  const t = useTranslatable()
  const locale = useLocale()
  const breadcrumbsItems = [{ page: `${t('common:files')}`, path: `/${locale}/administrator/files` }]
  const [searchParams, setSearchParams] = useSearchParams({
    page: '1',
    perPage: '10',
    query: '',
    status: '',
    storage: '',
  })
  const query = searchParams.get('query') || ''
  const status = searchParams.get('status') || ''
  const storage = searchParams.get('storage') || ''

  const initialValues: FormikValues = { query, status, storage }

  return (
    <Section
      title={t('common:files')}
      breadcrubms={breadcrumbsItems}
      icon={<FontAwesomeIcon icon={ProRegularSvgIcons.faFiles} />}
    >
      <Form
        initialValues={initialValues}
        onSubmit={async (values: FormikValues) => {
          setSearchParams((params) => {
            Object.entries(values).forEach(([key, value]) => {
              params.set(key, value)
            })

            return params
          })
        }}
        classNames={{
          form: 'w-full px-2 mt-3 mb-1',
        }}
        enableReinitialize
      >
        <FormSearch />
      </Form>

      <SuspenseWrapper>
        <TableContent searchParams={searchParams} />
      </SuspenseWrapper>
    </Section>
  )
}

function FormSearch(): React.ReactElement | null {
  const formik = useFormikContext<FormikValues>()
  const t = useTranslatable()
  const headers = useAuthenticatedHeaders()
  const [searchParams, setSearchParams] = useSearchParams()
  const status = searchParams.get('status') || ''
  const storage = searchParams.get('storage') || ''
  const query = searchParams.get('query') || ''
  const { data: fileDefinitions, isValidating: isFilesDefinitionsValidating } = useApi({
    endpoint: Api.getFilesDefinitions,
    params: React.useMemo(
      () => ({
        headers,
        suspense: false,
      }),
      [headers]
    ),
  })

  const fileStatusOptions = React.useMemo(() => {
    if (!fileDefinitions) return []
    return [{ value: '', label: t('common:any') }, ...fileDefinitions.statuses].map((status) => ({
      value: status.value || '',
      label: status.label,
    }))
  }, [fileDefinitions, t])

  const fileStorageOptions = React.useMemo(() => {
    if (!fileDefinitions) return []
    return [{ value: '', label: t('common:any') }, ...fileDefinitions.storages].map((storage) => ({
      value: storage.value || '',
      label: storage.label,
    }))
  }, [fileDefinitions, t])

  const isDirty = formik.values.query !== query || formik.values.status !== status || formik.values.storage !== storage
  const canCancel = query === '' && status === '' && storage === ''

  return (
    <fieldset disabled={formik.isSubmitting} className="flex w-full flex-col justify-between">
      <FormError />
      <div className="mb-3 flex w-full flex-wrap justify-between">
        <div className="mb-0 w-full min-w-[130px] md:w-[32%] xl:!mb-3">
          <TextInput
            type="text"
            label={t('common:search_for_keywords')}
            name="query"
            placeholder={t('common:search')}
          />
        </div>
        <div className="w-full min-w-[130px] md:w-[32%] xl:!mb-3">
          <SelectInput
            label={t('common:status')}
            name="status"
            options={fileStatusOptions}
            disabled={formik.isSubmitting || isFilesDefinitionsValidating}
            isLoading={isFilesDefinitionsValidating}
          />
        </div>
        <div className="w-full min-w-[130px] md:w-[32%] xl:!mb-3">
          <SelectInput
            label={t('common:storages')}
            name="storage"
            options={fileStorageOptions}
            disabled={formik.isSubmitting || isFilesDefinitionsValidating}
            isLoading={isFilesDefinitionsValidating}
          />
        </div>
      </div>

      <div className="flex w-full justify-end">
        <Button
          variant="red"
          disabled={formik.isSubmitting || !isDirty}
          type="submit"
          onClick={() => {
            formik.submitForm()
          }}
        >
          {t('common:search')}
        </Button>
        <Button
          variant="yellow"
          onClick={() => {
            setSearchParams({
              query: '',
              status: '',
              storage: '',
              page: '1',
              perPage: '10',
            })
            formik.resetForm()
            formik.submitForm()
          }}
          type="reset"
          disabled={formik.isSubmitting || canCancel}
          className="ml-2"
        >
          {t('common:cancel')}
        </Button>
      </div>
    </fieldset>
  )
}

function TableContent({ searchParams }: { searchParams: URLSearchParams }): JSX.Element | null {
  const locale = useLocale()
  const theme = useTheme()
  const headers = useAuthenticatedHeaders()
  const t = useTranslatable()
  const query = searchParams.get('query') || ''
  const status = searchParams.get('status') || ''
  const page = searchParams.get('page')!
  const perPage = searchParams.get('perPage')!
  const storage = searchParams.get('storage') || ''
  const { data: files } = useApi({
    endpoint: Api.getAdministratorFiles,
    params: React.useMemo(
      () => ({
        headers,
        query: {
          filters: {
            search: query!,
            status: status!,
            storage: storage!,
          },
          page: parseInt(page, 10),
          perPage: parseInt(perPage, 10),
        },
      }),
      [headers, perPage, query, status, page, storage]
    ),
  })

  const dateLocale = useDateLocale()

  if (!files || !files.data || files.data.length === 0) {
    return <NoContent header={t('error:records_not_found')} image={Warning} marginTop="5" />
  }

  return (
    <div className="mb-7">
      <PaginationTop pagination={files.meta.pagination} />
      <Table.Table className="mt-7">
        <Table.Thead>
          <Table.Tr>
            <Table.Th className="!pl-6">{t('message:subject')}</Table.Th>
            <Table.Th>{t('common:original_name')}</Table.Th>
            <Table.Th>{t('common:type')}</Table.Th>
            <Table.Th>{t('common:size')}</Table.Th>
            <Table.Th>{t('common:downloads')}</Table.Th>
            <Table.Th>{t('common:download')}</Table.Th>
            <Table.Th>{t('common:author')}</Table.Th>
            <Table.Th>{t('common:date')}</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {files.data.map((file) => (
            <tr key={file.id}>
              <Table.Td className="whitespace-nowrap">{file.title}</Table.Td>
              <Table.Td>{file.originalName}</Table.Td>
              <Table.Td>{file.extension}</Table.Td>
              <Table.Td>{formatFileSize(file.size)}</Table.Td>
              <Table.Td>{file.downloads}</Table.Td>
              <Table.Td>
                <MediaFilesDownload
                  mediaFileId={file.id}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="downloadMaterialButton"
                  className="flex size-7 items-center justify-center rounded-full p-1 hover:bg-table-icon-hover"
                >
                  <DownloadIcon />
                </MediaFilesDownload>
              </Table.Td>
              <Table.Td>
                <div className="flex flex-nowrap items-center">
                  <UserPhoto className="scale-[0.7]" user={file.author} />{' '}
                  <Link
                    to={`/${locale}/users/${file.author?.uid}`}
                    className="ml-2 whitespace-nowrap align-middle hover:underline"
                    data-tooltip-id={`${file.id}-author`}
                  >
                    <span className="ml-2 whitespace-normal text-primaryBlueLink">{file.author?.fullName}</span>
                  </Link>
                  <Tooltip
                    id={`${file.id}-author`}
                    place="top"
                    variant={theme === 'dark' ? 'dark' : 'light'}
                    className="m-0 p-1"
                  >
                    <p className="mb-0">{t('common:author_page')}</p>
                  </Tooltip>
                </div>
              </Table.Td>
              <Table.Td>
                {format(parseISO(file.createdAt), 'd MMMM yyyy, HH:mm:ss', {
                  locale: dateLocale,
                })}
              </Table.Td>
            </tr>
          ))}
        </Table.Tbody>
      </Table.Table>
      <PaginationBottom pagination={files.meta.pagination} />
    </div>
  )
}
